"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { navGroups, filterNavGroups } from "@/lib/navigation";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useAuth } from "@/hooks/useAuth";
import { useTranslations } from "@/hooks/useTranslations";
import { Settings, User, LogOut } from "lucide-react";
import { FeedbackModal } from "@/components/feedback/FeedbackModal";

// Color classes for navigation group headers
const getNavGroupColors = (color?: string) => {
  const colors: Record<string, { bg: string; text: string }> = {
    purple: {
      bg: "bg-purple-50/70 dark:bg-purple-950/30",
      text: "text-purple-700 dark:text-purple-300",
    },
    blue: {
      bg: "bg-blue-50/70 dark:bg-blue-950/30",
      text: "text-blue-700 dark:text-blue-300",
    },
    green: {
      bg: "bg-green-50/70 dark:bg-green-950/30",
      text: "text-green-700 dark:text-green-300",
    },
    orange: {
      bg: "bg-orange-50/70 dark:bg-orange-950/30",
      text: "text-orange-700 dark:text-orange-300",
    },
    cyan: {
      bg: "bg-cyan-50/70 dark:bg-cyan-950/30",
      text: "text-cyan-700 dark:text-cyan-300",
    },
  };
  return colors[color || "blue"] || colors.blue;
};

interface SidebarProps {
  organizationName?: string;
  organizationLogo?: string | null;
  userRole?: string;
  primaryColor?: string | null;
}

export function Sidebar({
  organizationName = "Your Business",
  organizationLogo,
  primaryColor,
}: SidebarProps) {
  const pathname = usePathname();
  const { features, staffPermissions } = useOrganization();
  const { isOwner, isStaff } = useAuth();
  const t = useTranslations('navigation');
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);

  // Translation map for navigation groups and items
  const getTranslatedLabel = (label: string): string => {
    const groupMap: Record<string, string> = {
      'Today': t('groups.today'),
      'Work': t('groups.work'),
      'Customers & Payments': t('groups.customersPayments'),
      'Help': t('groups.help'),
    };
    return groupMap[label] || label;
  };

  const getTranslatedName = (name: string): string => {
    const itemMap: Record<string, string> = {
      'AI Agent': t('items.aiAgent'),
      'Dashboard': t('dashboard'),
      'Getting Started': t('items.gettingStarted'),
      'Staff Tour': t('items.staffTour'),
      'Jobs': t('jobs'),
      'Schedule': t('schedule'),
      'Routes': t('items.routes'),
      'Time Tracking': t('items.timeTracking'),
      'Dispatch': t('items.dispatch'),
      'Clients': t('clients'),
      'Quotes': t('quotes'),
      'Invoices': t('invoices'),
      'Messages': t('items.messages'),
      'Help': t('items.help'),
    };
    return itemMap[name] || name;
  };

  // Fetch pending approvals count
  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const response = await fetch("/api/bookings/pending-count");
        if (response.ok) {
          const data = await response.json();
          setPendingApprovalsCount(data.count || 0);
        }
      } catch (error) {
        console.error("Failed to fetch pending approvals count:", error);
      }
    };

    fetchPendingCount();

    // Refresh count every 30 seconds
    const interval = setInterval(fetchPendingCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Filter navigation based on enabled features, staff permissions, and user role
  const filteredNavGroups = filterNavGroups(navGroups, features, staffPermissions, isOwner, isStaff);

  return (
    <div className="flex h-full w-64 flex-col border-r border-gray-200 bg-white shadow-lg dark:border-gray-800 dark:bg-gray-950" data-tour="sidebar">
      {/* Logo/Brand with gradient background */}
      <div className="flex min-h-16 items-center border-b border-blue-200 bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-2 dark:border-gray-800 dark:from-gray-900 dark:to-gray-800">
        <Link href="/dashboard" className="flex items-center gap-3 transition-transform hover:scale-105">
          {organizationLogo ? (
            <img
              src={organizationLogo}
              alt={organizationName}
              className="h-10 w-10 rounded-lg shadow-sm flex-shrink-0"
            />
          ) : (
            <div
              className="flex h-10 w-10 items-center justify-center rounded-lg font-bold text-white shadow-md transition-transform hover:shadow-lg flex-shrink-0"
              style={{
                backgroundColor: primaryColor || "var(--color-primary, #3b82f6)",
              }}
            >
              <span className="text-lg">{organizationName.charAt(0).toUpperCase()}</span>
            </div>
          )}
          <span className="text-lg font-bold text-gray-800 dark:text-white leading-tight break-words">
            {organizationName}
          </span>
        </Link>
      </div>

      {/* Navigation with groups */}
      <nav className="flex-1 px-3 py-6 overflow-y-auto" aria-label={t('mainNavigation')}>
        {filteredNavGroups.map((group, groupIndex) => {
          const groupColors = getNavGroupColors(group.color);
          return (
          <div key={group.label} className={cn(groupIndex > 0 && "mt-6")}>
            {/* Group Label */}
            <div className={cn("mb-2 px-3 py-1.5 rounded-md", groupColors.bg)}>
              <h3 className={cn("text-xs font-semibold uppercase tracking-wider", groupColors.text)}>
                {getTranslatedLabel(group.label)}
              </h3>
            </div>

            {/* Group Items */}
            <div className="space-y-1">
              {group.items.map((item) => {
                const isActive = pathname === item.href || pathname.startsWith(`${item.href}/`);
                const Icon = item.icon;
                const isBookings = item.href === "/bookings";
                const showBadge = isBookings && pendingApprovalsCount > 0;

                return (
                  <Link
                    key={item.name}
                    href={item.href}
                    className={cn(
                      "group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 min-h-[44px]",
                      isActive
                        ? "bg-primary text-primary-foreground shadow-md"
                        : "text-foreground/70 hover:bg-accent hover:text-accent-foreground"
                    )}
                    style={
                      isActive && primaryColor
                        ? { backgroundColor: primaryColor }
                        : undefined
                    }
                  >
                    {/* Left accent bar for active item */}
                    {isActive && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 rounded-r-full bg-primary-foreground/30" />
                    )}

                    <Icon
                      className={cn(
                        "h-5 w-5 flex-shrink-0 transition-colors duration-200",
                        isActive
                          ? "text-primary-foreground"
                          : "text-primary/70 group-hover:text-primary"
                      )}
                    />
                    <span className="truncate flex-1">{getTranslatedName(item.name)}</span>

                    {/* Pending approvals badge */}
                    {showBadge && (
                      <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-500 px-1.5 text-xs font-bold text-white">
                        {pendingApprovalsCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        );
        })}
      </nav>

      {/* Footer with Settings/Profile links for mobile */}
      <div className="border-t border-gray-200 dark:border-gray-800 px-3 py-3 bg-gray-50 dark:bg-gray-900 space-y-1">
        <Link
          href="/settings/profile"
          className={cn(
            "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 min-h-[44px]",
            pathname === "/settings/profile"
              ? "bg-primary text-primary-foreground shadow-md"
              : "text-foreground/70 hover:bg-accent hover:text-accent-foreground"
          )}
        >
          <User className="h-5 w-5" />
          <span>{t('profile')}</span>
        </Link>
        <Link
          href="/settings"
          className={cn(
            "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 min-h-[44px]",
            pathname === "/settings" || (pathname.startsWith("/settings/") && pathname !== "/settings/profile")
              ? "bg-primary text-primary-foreground shadow-md"
              : "text-foreground/70 hover:bg-accent hover:text-accent-foreground"
          )}
        >
          <Settings className="h-5 w-5" />
          <span>{t('settings')}</span>
        </Link>
        <FeedbackModal />
        <a
          href="/api/auth/signout"
          className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 min-h-[44px] text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20"
        >
          <LogOut className="h-5 w-5" />
          <span>{t('logOut')}</span>
        </a>
      </div>
    </div>
  );
}
